/**
 * Verbatim guest pull-quotes pulled from actual episode transcripts.
 * Keyed by episode youtube_id. Each includes a timestamp so we can deep-link
 * into the exact moment on YouTube.
 */
export const GUEST_QUOTES = {
  JNpeGmzPIKw: {
    quote:
      'You have to be able to strike that balance between mission accomplishment and welfare of your men.',
    timestamp: '22:27',
    speaker: 'Col. Francel Padilla',
  },
  'Z9quGBSr-Hw': {
    quote:
      'Allowing large artificial intelligence models to be open sourced is the equivalence of allowing nuclear weapons to be sold at the corner shop.',
    timestamp: '5:55',
    speaker: 'Dr. Demetrius Floudas',
  },
  o1iMR6V0LZQ: {
    quote:
      'We must be sure that the information provided by the use of AI is reliable and not a fake news.',
    timestamp: '12:17',
    speaker: 'Prof. Dr. Christoph Spengel',
  },
};

/**
 * Extract a punchy highlight sentence from an episode's show notes as a
 * fallback for episodes that don't have a verified transcript quote yet.
 */
const stripMarkers = (text) =>
  (text || '')
    .replace(/\*\*/g, '')
    .replace(/\s*\n+\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const looksLikeQuestion = (s) => /\?$/.test(s);

export const extractHighlight = (description) => {
  const clean = stripMarkers(description);
  if (!clean) return null;
  const sentences = clean
    .split(/(?<=[.!?])\s+(?=[A-Z"])/)
    .map((s) => s.trim())
    .filter((s) => s.length >= 40 && s.length <= 220);
  if (!sentences.length) return null;
  const priorityHints = /\b(the real|the truth|what if|why|the biggest|the future|the secret|shatters|reveal|redefin|transform|reimagin|redefine|game-chang)\b/i;
  const scored = sentences.map((s) => ({
    s,
    score: (priorityHints.test(s) ? 2 : 0) + (looksLikeQuestion(s) ? 1 : 0) + (s.length > 90 && s.length < 180 ? 1 : 0),
  }));
  scored.sort((a, b) => b.score - a.score);
  return scored[0].s;
};

/** Convert "M:SS" or "H:MM:SS" -> total seconds (integer). */
export const timestampToSeconds = (ts) => {
  if (!ts) return 0;
  const parts = ts.split(':').map((n) => parseInt(n, 10) || 0);
  return parts.reverse().reduce((a, v, i) => a + v * [1, 60, 3600][i], 0);
};

/** Build a YouTube deep-link that jumps directly to the timestamp. */
export const youtubeTimestampUrl = (youtubeId, timestamp) => {
  if (!youtubeId) return '';
  const s = timestampToSeconds(timestamp);
  return s > 0
    ? `https://www.youtube.com/watch?v=${youtubeId}&t=${s}s`
    : `https://www.youtube.com/watch?v=${youtubeId}`;
};

/** Look up a verbatim quote by episode.youtube_id */
export const getQuoteFor = (ep) => (ep && ep.youtube_id ? GUEST_QUOTES[ep.youtube_id] : null);

/**
 * Return a { quote, timestamp?, verbatim } object suitable for the Key Insight
 * block. Falls back to an extracted highlight from the description if no
 * verbatim quote exists yet.
 */
export const getHighlightFor = (ep) => {
  const verbatim = getQuoteFor(ep);
  if (verbatim) return { ...verbatim, verbatim: true };
  const extracted = extractHighlight(ep && ep.description);
  if (!extracted) return null;
  return { quote: extracted, timestamp: null, verbatim: false };
};
